<form class="ngsild-form" [formGroup]="form">
  <div class="ngsild-form__field">
    <div class="ngsild-tags-header">
      <label class="ngsild-form__label" for="ngsild-tags-input">
        Tags <span class="ngsild-form__required">*</span>
      </label>
      <p class="ngsild-form__help ngsild-form__help--inline">
        Each tag can be up to {{ tagMaxLength }} characters (letters and numbers only).
      </p>
    </div>
    <div
      class="ngsild-tags"
      [class.ngsild-tags--error]="showError('tags')"
    >
      <div
        class="ngsild-tags__input-row"
        [class.ngsild-tags__input-row--error]="showError('tags')"
      >
        <input
          id="ngsild-tags-input"
          type="text"
          class="ngsild-tags__input"
          placeholder="Enter a tag"
          [maxlength]="tagMaxLength"
          [(ngModel)]="tagInput"
          [ngModelOptions]="{ standalone: true }"
          (keydown)="onTagInputKeydown($event)"
          (input)="onTagInputChange()"
          (paste)="onTagInputPaste($event)"
        />
        <button
          type="button"
          class="ngsild-tags__add"
          title="Add tag"
          [disabled]="!canAddTag()"
          (click)="addTag()"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M8 3V13M3 8H13"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
        </button>
      </div>
      @if (tags.length > 0) {
        <div class="ngsild-tags__list">
          @for (tag of tags; track tag) {
            <span class="ngsild-tags__chip">
              {{ tag }}
              <button
                type="button"
                class="ngsild-tags__chip-remove"
                aria-label="Remove tag"
                (click)="removeTag(tag)"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path
                    d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                  />
                </svg>
              </button>
            </span>
          }
        </div>
      }
    </div>
    @if (showError('tags')) {
      <p class="ngsild-form__error">At least one tag is required</p>
    }
  </div>

  <div class="ngsild-form__row">
    <div class="ngsild-form__field">
      <label class="ngsild-form__label" for="ngsild-year-range">
        Year Range <span class="ngsild-form__required">*</span>
      </label>
      <lib-ngsild-year-range-field
        id="ngsild-year-range"
        formControlName="yearRange"
        placeholder="Choose year range"
        [hasError]="showError('yearRange')"
      />
      @if (showError('yearRange')) {
        <p class="ngsild-form__error">Year range is required</p>
      }
    </div>

    <div class="ngsild-form__field">
      <label class="ngsild-form__label" for="ngsild-geo-coverage">
        Geo Coverage <span class="ngsild-form__required">*</span>
      </label>
      <input
        id="ngsild-geo-coverage"
        type="text"
        class="ngsild-form__input ngsild-form__input--single-line"
        formControlName="geoCoverage"
        placeholder="Enter geo coverage"
        [class.ngsild-form__input--error]="showError('geoCoverage')"
      />
      @if (showError('geoCoverage')) {
        <p class="ngsild-form__error">Geo coverage is required</p>
      }
    </div>
  </div>

  <div class="ngsild-form__row">
    <div class="ngsild-form__field">
      <label class="ngsild-form__label" for="ngsild-upload-frequency">
        Upload Frequency <span class="ngsild-form__required">*</span>
      </label>
      <div class="custom-dropdown-wrapper">
        <div
          id="ngsild-upload-frequency"
          class="custom-dropdown-trigger"
          [class.error]="showError('uploadFrequency')"
          [class.open]="isDropdownOpen('uploadFrequency')"
          (click)="toggleDropdown('uploadFrequency')"
        >
          <div class="trigger-content">
            @if (!form.controls.uploadFrequency.value) {
              <span class="placeholder">Select upload frequency</span>
            } @else {
              <span class="selected-value">{{ getSelectedLabel('uploadFrequency') }}</span>
            }
          </div>
          <svg class="dropdown-arrow" [class.open]="isDropdownOpen('uploadFrequency')" width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true">
            <path d="M1 1L6 6L11 1" stroke="#666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        @if (isDropdownOpen('uploadFrequency')) {
          <div class="custom-dropdown-panel" (click)="$event.stopPropagation()">
            @for (opt of uploadFrequencyOptions; track opt.value) {
              <div class="dropdown-option" (click)="selectOption('uploadFrequency', opt, 'uploadFrequency')">
                <div class="checkbox-wrapper">
                  <input type="checkbox" class="checkbox-custom" [checked]="isSelected('uploadFrequency', opt.value)" readonly tabindex="-1"/>
                </div>
                <span class="option-label">{{ opt.label }}</span>
              </div>
            }
          </div>
        }
      </div>
      @if (showError('uploadFrequency')) {
        <p class="ngsild-form__error">Upload frequency is required</p>
      }
    </div>

    <div class="ngsild-form__field">
      <label class="ngsild-form__label" for="ngsild-license">
        License <span class="ngsild-form__required">*</span>
      </label>
      <div class="custom-dropdown-wrapper">
        <div
          id="ngsild-license"
          class="custom-dropdown-trigger"
          [class.error]="showError('license')"
          [class.open]="isDropdownOpen('license')"
          (click)="toggleDropdown('license')"
        >
          <div class="trigger-content">
            @if (!form.controls.license.value) {
              <span class="placeholder">Select license</span>
            } @else if (form.controls.license.value === 'Other') {
              <span class="selected-value">Other</span>
            } @else {
              <span class="selected-value">{{ getSelectedLabel('license') }}</span>
            }
          </div>
          <svg class="dropdown-arrow" [class.open]="isDropdownOpen('license')" width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true">
            <path d="M1 1L6 6L11 1" stroke="#666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        @if (isDropdownOpen('license')) {
          <div class="custom-dropdown-panel" (click)="$event.stopPropagation()">
            @for (opt of licenseOptions; track opt.value) {
              <div class="dropdown-option" (click)="selectOption('license', opt, 'license')">
                <div class="checkbox-wrapper">
                  <input type="checkbox" class="checkbox-custom" [checked]="isSelected('license', opt.value)" readonly tabindex="-1"/>
                </div>
                <span class="option-label">{{ opt.label }}</span>
              </div>
            }
          </div>
        }
      </div>
      @if (showError('license')) {
        <p class="ngsild-form__error">License is required</p>
      }
      @if (isOtherLicenseSelected()) {
        <div class="ngsild-form__field ngsild-form__field--nested">
          <label class="ngsild-form__label" for="ngsild-other-license">
            Specify License <span class="ngsild-form__required">*</span>
          </label>
          <input
            id="ngsild-other-license"
            type="text"
            class="ngsild-form__input ngsild-form__input--single-line"
            formControlName="otherLicense"
            placeholder="Enter license name"
            [class.ngsild-form__input--error]="showError('otherLicense')"
          />
          @if (showError('otherLicense')) {
            <p class="ngsild-form__error">Please specify the license name</p>
          }
        </div>
      }
    </div>
  </div>

  @if (isOgcFeature()) {
    <div class="ngsild-form__row">
      <div class="ngsild-form__field">
        <label class="ngsild-form__label">
          Dataset Type <span class="ngsild-form__required">*</span>
        </label>
        <div class="custom-dropdown-trigger disabled" aria-readonly="true">
          <div class="trigger-content">
            <span class="selected-value">semi Structured</span>
          </div>
          <svg class="dropdown-arrow" width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true">
            <path d="M1 1L6 6L11 1" stroke="#666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <p class="ngsild-form__help">Required for OGC Feature datasets — cannot be changed.</p>
      </div>

      <div class="ngsild-form__field">
        <label class="ngsild-form__label">
          File Format <span class="ngsild-form__required">*</span>
        </label>
        <div class="custom-dropdown-trigger disabled" aria-readonly="true">
          <div class="trigger-content">
            <span class="selected-value">gpkg</span>
          </div>
          <svg class="dropdown-arrow" width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true">
            <path d="M1 1L6 6L11 1" stroke="#666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <p class="ngsild-form__help">GeoPackage (.gpkg) is required for OGC Feature datasets — cannot be changed.</p>
      </div>
    </div>
  } @else {
    <div class="ngsild-form__row">
      <div class="ngsild-form__field">
        <label class="ngsild-form__label" for="ngsild-dataset-type">
          Dataset Type <span class="ngsild-form__required">*</span>
        </label>
        <div class="custom-dropdown-wrapper">
          <div
            id="ngsild-dataset-type"
            class="custom-dropdown-trigger"
            [class.error]="showError('datasetType')"
            [class.open]="isDropdownOpen('datasetType')"
            (click)="toggleDropdown('datasetType')"
          >
            <div class="trigger-content">
              @if (!form.controls.datasetType.value) {
                <span class="placeholder">Select dataset type</span>
              } @else {
                <span class="selected-value">{{ getSelectedLabel('datasetType') }}</span>
              }
            </div>
            <svg class="dropdown-arrow" [class.open]="isDropdownOpen('datasetType')" width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true">
              <path d="M1 1L6 6L11 1" stroke="#666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          @if (isDropdownOpen('datasetType')) {
            <div class="custom-dropdown-panel" (click)="$event.stopPropagation()">
              @for (opt of datasetTypeOptions; track opt.value) {
                <div class="dropdown-option" (click)="selectOption('datasetType', opt, 'datasetType')">
                  <div class="checkbox-wrapper">
                    <input type="checkbox" class="checkbox-custom" [checked]="isSelected('datasetType', opt.value)" readonly tabindex="-1"/>
                  </div>
                  <span class="option-label">{{ opt.label }}</span>
                </div>
              }
            </div>
          }
        </div>
        @if (showError('datasetType')) {
          <p class="ngsild-form__error">Dataset type is required</p>
        }
      </div>

      <div class="ngsild-form__field">
        <label class="ngsild-form__label" for="ngsild-file-format">
          File Format <span class="ngsild-form__required">*</span>
        </label>
        <div class="custom-dropdown-wrapper">
          <div
            id="ngsild-file-format"
            class="custom-dropdown-trigger"
            [class.error]="showError('fileFormat')"
            [class.open]="isDropdownOpen('fileFormat')"
            [class.disabled]="isFileFormatDisabled()"
            (click)="toggleDropdown('fileFormat')"
          >
            <div class="trigger-content">
              @if (!form.controls.fileFormat.value) {
                <span class="placeholder">{{ fileFormatPlaceholder() }}</span>
              } @else {
                <span class="selected-value">{{ getSelectedLabel('fileFormat') }}</span>
              }
            </div>
            <svg class="dropdown-arrow" [class.open]="isDropdownOpen('fileFormat')" width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true">
              <path d="M1 1L6 6L11 1" stroke="#666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          @if (isDropdownOpen('fileFormat') && !isFileFormatDisabled()) {
            <div class="custom-dropdown-panel" (click)="$event.stopPropagation()">
              @for (opt of fileFormatOptions; track opt.value) {
                <div class="dropdown-option" (click)="selectOption('fileFormat', opt, 'fileFormat')">
                  <div class="checkbox-wrapper">
                    <input type="checkbox" class="checkbox-custom" [checked]="isSelected('fileFormat', opt.value)" readonly tabindex="-1"/>
                  </div>
                  <span class="option-label">{{ opt.label }}</span>
                </div>
              }
            </div>
          }
        </div>
        @if (showError('fileFormat')) {
          <p class="ngsild-form__error">File format is required</p>
        }
      </div>
    </div>
  }

  <!-- ── Data Descriptor Section ─────────────────────────────────────────── -->
  <div class="ngsild-form__field">
    <div class="ngsild-descriptor-header">
      <label class="ngsild-form__label">Data Descriptor</label>
      <button
        type="button"
        class="ngsild-btn-add-descriptor"
        (click)="openDescriptorEditor()"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M8 3V13M3 8H13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        Add Descriptor
      </button>
    </div>

    @if (descriptorRows.length > 0 && descriptorRows[0].attributeName) {
      <div class="ngsild-descriptors-table-wrapper">
        <table class="ngsild-descriptors-table">
          <thead>
            <tr>
              <th>Attribute Name</th>
              <th>Parameter Type</th>
              <th>Schema Type</th>
              <th class="ngsild-descriptors-table__th-action">Action</th>
            </tr>
          </thead>
          <tbody>
            @for (row of descriptorRows; track $index; let i = $index) {
              @if (row.attributeName) {
                <tr [class.ngsild-descriptors-table__row--dropdown-open]="isTableActionOpen(i)">
                  <td>{{ row.attributeName }}</td>
                  <td>{{ getParameterTypeLabel(row) }}</td>
                  <td>{{ getSchemaTypeLabel(row) }}</td>
                  <td class="ngsild-descriptors-table__td-action">
                    <div
                      class="ngsild-action-cell"
                      [class.ngsild-action-cell--open]="isTableActionOpen(i)"
                    >
                      <button
                        type="button"
                        class="ngsild-action-btn"
                        [class.ngsild-action-btn--active]="isTableActionOpen(i)"
                        (click)="toggleTableActionDropdown($event, i)"
                      >
                        Action
                      </button>
                      @if (isTableActionOpen(i)) {
                        <div
                          class="ngsild-action-dropdown"
                          [attr.data-ngsild-action-dropdown]="i"
                          (click)="$event.stopPropagation()"
                        >
                          <ul class="ngsild-action-dropdown__list">
                            <li>
                              <button
                                type="button"
                                class="ngsild-action-dropdown__item"
                                (click)="editDescriptorFromTable(i)"
                              >
                                Edit
                              </button>
                            </li>
                            <li>
                              <button
                                type="button"
                                class="ngsild-action-dropdown__item ngsild-action-dropdown__item--danger"
                                (click)="deleteDescriptorFromTable(i)"
                              >
                                Delete
                              </button>
                            </li>
                          </ul>
                        </div>
                      }
                    </div>
                  </td>
                </tr>
              }
            }
          </tbody>
        </table>
      </div>
    } @else {
      <div class="ngsild-no-descriptors">
        <p>No data descriptors added. Click "Add Descriptor" to add one.</p>
      </div>
    }
  </div>

  <!-- ── Sample Data Section ─────────────────────────────────────────────── -->
  <div class="ngsild-form__field">
    <div class="ngsild-descriptor-header ngsild-sample-header">
      <label class="ngsild-form__label">Sample Data</label>
      <div class="ngsild-sample-header__actions">
        <input
          #sampleJsonInput
          type="file"
          accept=".json,application/json"
          class="ngsild-sample-file-input"
          (change)="onSampleJsonFileSelected($event)"
        />
        <button
          type="button"
          class="ngsild-btn-add-descriptor"
          (click)="triggerSampleJsonUpload(sampleJsonInput)"
        >
          Upload JSON
        </button>
        <button
          type="button"
          class="ngsild-btn-add-descriptor"
          (click)="onAddSampleDataClick()"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M8 3V13M3 8H13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Add Sample Data
        </button>
      </div>
    </div>

    @if (hasSampleData) {
      <div class="ngsild-sample-card">
        <div class="ngsild-sample-card__info">
          <span class="ngsild-sample-card__badge">JSON</span>
          <div class="ngsild-sample-card__text">
            <p class="ngsild-sample-card__title">
              {{ sampleUploadedFileName || 'Sample JSON added' }}
            </p>
            <p class="ngsild-sample-card__hint">Click Preview/Edit to view or edit the sample data</p>
          </div>
        </div>
        <div class="ngsild-sample-card__actions">
          <button
            type="button"
            class="ngsild-sample-opaque__preview"
            (click)="openSamplePreview()"
          >
            Preview/Edit
          </button>
          <button
            type="button"
            class="ngsild-sample-opaque__clear"
            (click)="clearSampleData()"
          >
            Delete
          </button>
        </div>
      </div>
    } @else {
      <div class="ngsild-no-descriptors">
        <p>No sample data added. Upload a JSON file (max 2 MB) or click "Add Sample Data".</p>
      </div>
    }

    @if (sampleUploadError) {
      <p class="ngsild-form__error">{{ sampleUploadError }}</p>
    }
  </div>

  <div class="ngsild-form__field">
    <lib-additional-info />
  </div>
</form>

<!-- ── Data Descriptor Editor Modal ──────────────────────────────────────── -->
@if (isDescriptorEditorOpen) {
  <div class="ngsild-modal-overlay" (click)="closeDescriptorEditor()">
    <div class="ngsild-modal ngsild-modal--editor" (click)="$event.stopPropagation()">

      <!-- Header -->
      <div class="ngsild-modal__header">
        <h3 class="ngsild-modal__title">
          Add Data Descriptor
        </h3>
        <button type="button" class="ngsild-modal__close" aria-label="Close" (click)="closeDescriptorEditor()">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
            <path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      </div>

      <div class="ngsild-modal__body ngsild-modal__body--editor">
        <div class="ngsild-editor-layout">
          <div class="ngsild-editor-layout__sidebar-body">
            <ul
              class="ngsild-editor-list"
              [class.ngsild-editor-list--capped]="editorRows.length > 5"
              role="listbox"
              aria-label="Data descriptors"
            >
              @for (row of editorRows; track $index; let i = $index) {
                <li
                  class="ngsild-editor-list__item"
                  [class.ngsild-editor-list__item--selected]="isEditorRowSelected(i)"
                  role="option"
                  [attr.aria-selected]="isEditorRowSelected(i)"
                >
                  <button
                    type="button"
                    class="ngsild-editor-list__select"
                    (click)="selectEditorRow(i)"
                  >
                    <span class="ngsild-editor-list__title">{{ getAccordionTitle(row, i) }}</span>
                    <span class="ngsild-editor-list__subtitle">{{ getAccordionSubtitle(row) }}</span>
                  </button>
                  @if (editorRows.length > 1) {
                    <button
                      type="button"
                      class="ngsild-editor-list__remove"
                      title="Remove data descriptor"
                      aria-label="Remove data descriptor"
                      (click)="removeEditorRow(i); $event.stopPropagation()"
                    >
                      <svg class="ngsild-icon-trash" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M3 6h18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M10 11v6M14 11v6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                  }
                </li>
              }
            </ul>
          </div>

          <section
            class="ngsild-editor-detail"
            aria-label="Descriptor data"
            (scroll)="onEditorDetailScroll()"
          >
            @if (selectedEditorRowIndex() !== null && editorRows[selectedEditorRowIndex()!]; as row) {
              @let i = selectedEditorRowIndex()!;
              <div class="ngsild-param-block ngsild-param-block--editor ngsild-form">
                <div class="ngsild-param-block__field ngsild-param-block__field--full">
                  <label class="ngsild-param-block__label">
                    Attribute Name <span class="ngsild-form__required">*</span>
                  </label>
                  <input
                    type="text"
                    class="ngsild-form__input ngsild-form__input--single-line"
                    [class.ngsild-form__input--error]="row.validations.isAttributeNameValid || row.validations.isAttributeNameDuplicate"
                    [value]="row.attributeName"
                    placeholder="Enter attribute name"
                    (input)="onAttributeNameChange(i, $any($event.target).value)"
                  />
                  @if (row.validations.isAttributeNameValid) {
                    <p class="ngsild-form__error">Attribute name is required</p>
                  }
                  @if (row.validations.isAttributeNameDuplicate) {
                    <p class="ngsild-form__error">Attribute name must be unique</p>
                  }
                </div>

                <div class="ngsild-param-block__grid">
                  <div class="ngsild-param-block__field">
                    <label class="ngsild-param-block__label">
                      Parameter Type <span class="ngsild-form__required">*</span>
                    </label>
                    <div class="custom-dropdown-wrapper">
                      <div
                        class="custom-dropdown-trigger"
                        [class.error]="row.validations.isParameterTypeValid"
                        [class.open]="isEditorDropdownOpen(i, 'parameterType')"
                        (click)="toggleEditorDropdown($event, i, 'parameterType')"
                      >
                        <div class="trigger-content">
                          @if (!getParameterTypeLabel(row)) {
                            <span class="placeholder">Select Parameter type</span>
                          } @else {
                            <span class="selected-value">{{ getParameterTypeLabel(row) }}</span>
                          }
                        </div>
                        <svg class="dropdown-arrow" [class.open]="isEditorDropdownOpen(i, 'parameterType')" width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true">
                          <path d="M1 1L6 6L11 1" stroke="#666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                      </div>
                      @if (isEditorDropdownOpen(i, 'parameterType')) {
                        <div
                          class="custom-dropdown-panel ngsild-editor-dropdown-panel"
                          [attr.data-ngsild-editor-dropdown]="editorDropdownKey(i, 'parameterType')"
                          (click)="$event.stopPropagation()"
                        >
                          @for (opt of parameterTypeOptions; track opt.item_id) {
                            <div class="dropdown-option" (click)="selectEditorParameterType(i, opt)">
                              <div class="checkbox-wrapper">
                                <input type="checkbox" class="checkbox-custom" [checked]="isParameterTypeSelected(row, opt.item_id)" readonly tabindex="-1"/>
                              </div>
                              <span class="option-label">{{ opt.item_text }}</span>
                            </div>
                          }
                        </div>
                      }
                    </div>
                    @if (row.validations.isParameterTypeValid) {
                      <p class="ngsild-form__error">Parameter type is required</p>
                    }
                  </div>

                  <div class="ngsild-param-block__field">
                    <label class="ngsild-param-block__label">
                      Schema Type <span class="ngsild-form__required">*</span>
                    </label>
                    <div class="custom-dropdown-wrapper">
                      <div
                        class="custom-dropdown-trigger"
                        [class.error]="row.validations.isDataSchemaTypeValid"
                        [class.open]="isEditorDropdownOpen(i, 'dataSchemaType')"
                        (click)="toggleEditorDropdown($event, i, 'dataSchemaType')"
                      >
                        <div class="trigger-content">
                          @if (!getSchemaTypeLabel(row)) {
                            <span class="placeholder">Select schema type</span>
                          } @else {
                            <span class="selected-value">{{ getSchemaTypeLabel(row) }}</span>
                          }
                        </div>
                        <svg class="dropdown-arrow" [class.open]="isEditorDropdownOpen(i, 'dataSchemaType')" width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true">
                          <path d="M1 1L6 6L11 1" stroke="#666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                      </div>
                      @if (isEditorDropdownOpen(i, 'dataSchemaType')) {
                        <div
                          class="custom-dropdown-panel ngsild-editor-dropdown-panel"
                          [attr.data-ngsild-editor-dropdown]="editorDropdownKey(i, 'dataSchemaType')"
                          (click)="$event.stopPropagation()"
                        >
                          @for (opt of schemaTypeOptions; track opt.item_id) {
                            <div class="dropdown-option" (click)="selectEditorSchemaType(i, opt)">
                              <div class="checkbox-wrapper">
                                <input type="checkbox" class="checkbox-custom" [checked]="isSchemaTypeSelected(row, opt.item_id)" readonly tabindex="-1"/>
                              </div>
                              <span class="option-label">{{ opt.item_text }}</span>
                            </div>
                          }
                        </div>
                      }
                    </div>
                    @if (row.validations.isDataSchemaTypeValid) {
                      <p class="ngsild-form__error">Schema type is required</p>
                    }
                  </div>

                  @if (isNumberSchemaRow(row)) {
                    <div class="ngsild-param-block__field">
                      <label class="ngsild-param-block__label">
                        Description <span class="ngsild-form__required">*</span>
                      </label>
                      <input
                        type="text"
                        class="ngsild-form__input ngsild-form__input--single-line"
                        [class.ngsild-form__input--error]="row.validations.isDataSchemaNumberTypeValid"
                        [value]="row.attributeObject.description ?? ''"
                        placeholder="Enter description"
                        (input)="onDescriptionChange(i, $any($event.target).value)"
                      />
                      @if (row.validations.isDataSchemaNumberTypeValid) {
                        <p class="ngsild-form__error">Description is required</p>
                      }
                    </div>

                    <div class="ngsild-param-block__field">
                      <label class="ngsild-param-block__label">
                        Unit Code <span class="ngsild-form__required">*</span>
                      </label>
                      <input
                        type="text"
                        class="ngsild-form__input ngsild-form__input--single-line"
                        [class.ngsild-form__input--error]="row.validations.isDataSchemaNumberUnitCodeTypeValid"
                        [value]="row.attributeObject.unitCode ?? ''"
                        placeholder="e.g. GM3"
                        (input)="onUnitCodeChange(i, $any($event.target).value)"
                      />
                      @if (row.validations.isDataSchemaNumberUnitCodeTypeValid) {
                        <p class="ngsild-form__error">Unit code is required</p>
                      }
                    </div>

                    <div class="ngsild-param-block__field ngsild-param-block__field--full">
                      <label class="ngsild-param-block__label">
                        Unit Text <span class="ngsild-form__required">*</span>
                      </label>
                      <input
                        type="text"
                        class="ngsild-form__input ngsild-form__input--single-line"
                        [class.ngsild-form__input--error]="row.validations.isDataSchemaNumberUnitTextValid"
                        [value]="row.attributeObject.unitText ?? ''"
                        placeholder="e.g. microgram per cubic metre"
                        (input)="onUnitTextChange(i, $any($event.target).value)"
                      />
                      @if (row.validations.isDataSchemaNumberUnitTextValid) {
                        <p class="ngsild-form__error">Unit text is required</p>
                      }
                    </div>
                  }

                  @if (hasSchemaSelected(row) && !isNumberSchemaRow(row)) {
                    <div class="ngsild-param-block__field ngsild-param-block__field--full">
                      <label class="ngsild-param-block__label">
                        Description <span class="ngsild-form__required">*</span>
                      </label>
                      <input
                        type="text"
                        class="ngsild-form__input ngsild-form__input--single-line"
                        [class.ngsild-form__input--error]="row.validations.isDataSchemaOthersDescriptionValid"
                        [value]="row.attributeObject.othersDescription ?? ''"
                        placeholder="Enter description"
                        (input)="onDescriptionChange(i, $any($event.target).value)"
                      />
                      @if (row.validations.isDataSchemaOthersDescriptionValid) {
                        <p class="ngsild-form__error">Description is required</p>
                      }
                    </div>
                  }
                </div>
              </div>
            } @else {
              <div class="ngsild-editor-detail__empty">
                <p>Select a descriptor from the list or add a new one.</p>
              </div>
            }
          </section>
        </div>
      </div>

      <!-- Footer -->
      <div class="ngsild-modal__footer ngsild-modal__footer--editor">
        <button type="button" class="ngsild-modal-btn ngsild-modal-btn--cancel" (click)="closeDescriptorEditor()">
          Cancel
        </button>
        <div class="ngsild-modal__footer-actions">
          <button
            type="button"
            class="ngsild-btn-add-row ngsild-btn-add-row--footer"
            [disabled]="!isAddEditorRowEnabled()"
            (click)="addEditorRow()"
          >
            <span class="ngsild-btn-add-row__icon" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="7.5" stroke="currentColor" stroke-width="1.5"/>
                <path d="M10 6.5v7M6.5 10h7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </span>
            Add New
          </button>
          <button
            type="button"
            class="ngsild-modal-btn ngsild-modal-btn--save"
            [disabled]="!isDescriptorEditorSaveEnabled()"
            (click)="saveDescriptorEditor(); $event.stopPropagation()"
          >
            Save
          </button>
        </div>
      </div>

    </div>
  </div>
}

<!-- ── Sample Data JSON Editor Modal ───────────────────────────────────── -->
@if (isSampleEditorOpen) {
  <div
    class="ngsild-modal-overlay"
    data-ngsild-sample-overlay
    (click)="closeSampleEditor()"
  >
    <div class="ngsild-modal ngsild-modal--sample-code" (click)="$event.stopPropagation()">
      <div class="ngsild-modal__header">
        <h3 class="ngsild-modal__title">Add Sample Data</h3>
        <button type="button" class="ngsild-modal__close" aria-label="Close" (click)="closeSampleEditor()">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
            <path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      </div>

      <div class="ngsild-modal__body ngsild-sample-code-body">
        <p class="ngsild-form__help">Type or paste a single JSON object. Max size: 2 MB.</p>
        <textarea
          class="ngsild-sample-code-editor"
          [class.ngsild-sample-code-editor--error]="!!sampleEditorError"
          [value]="sampleEditorText"
          (input)="onSampleEditorTextChange($any($event.target).value)"
          spellcheck="false"
          aria-label="Sample data JSON"
          placeholder='{"key": "value"}'
        ></textarea>
        @if (sampleEditorError) {
          <p class="ngsild-form__error">{{ sampleEditorError }}</p>
        }
      </div>

      <div class="ngsild-modal__footer">
        <button type="button" class="ngsild-modal-btn ngsild-modal-btn--cancel" (click)="closeSampleEditor()">
          Cancel
        </button>
        <button
          type="button"
          class="ngsild-modal-btn ngsild-modal-btn--save"
          (click)="saveSampleEditor(); $event.stopPropagation()"
        >
          Save
        </button>
      </div>
    </div>
  </div>
}

<!-- ── Sample Data Preview Modal ─────────────────────────────────────────── -->
@if (isSamplePreviewOpen) {
  <div
    class="ngsild-modal-overlay"
    data-ngsild-sample-preview-overlay
    (click)="closeSamplePreview()"
  >
    <div class="ngsild-modal ngsild-modal--sample-preview" (click)="$event.stopPropagation()">
      <div class="ngsild-modal__header">
        <h3 class="ngsild-modal__title">Sample Data Preview</h3>
        <button type="button" class="ngsild-modal__close" aria-label="Close" (click)="closeSamplePreview()">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
            <path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
      <div class="ngsild-modal__body ngsild-sample-preview-body">
        @if (sampleUploadedFileName) {
          <p class="ngsild-sample-preview-file">{{ sampleUploadedFileName }}</p>
        }
        @if (formattedSampleDataPreview) {
          <pre class="ngsild-sample-preview-json">{{ formattedSampleDataPreview }}</pre>
        } @else {
          <p class="ngsild-sample-preview-empty">No sample data available.</p>
        }
      </div>
      <div class="ngsild-modal__footer ngsild-modal__footer--sample-preview">
        <button type="button" class="ngsild-modal-btn ngsild-modal-btn--cancel" (click)="closeSamplePreview()">
          Close
        </button>
        <button type="button" class="ngsild-modal-btn ngsild-modal-btn--save" (click)="editSampleFromPreview()">
          Edit
        </button>
      </div>
    </div>
  </div>
}
